import { Box, Paper, Stack, Typography } from "@mui/material";
import { Grid } from "@mui/system";
import { ArcElement, Chart as ChartJS, Legend, Tooltip } from "chart.js";
import {
  useBackofficeConstant,
  useBackofficeConstantDico,
} from "components/context/ConstantProvider";
import { LabelValueForText } from "components/ui/atoms/LabelValue";
import { simpleNumberFormatter } from "components/ui/customs/viewFields/percentageFieldView";
import { Pie } from "react-chartjs-2";
import { SlideStyle } from "../slideDesign/Slide";
import SlideSectionText from "../slideDesign/SlideSectionText";
import SlideLabelValue from "../slideDesign/SlideLabelValue";
import { useTranslation } from "react-i18next";
import { t } from "i18next";

ChartJS.register(ArcElement, Tooltip, Legend);

const ring_fund_nfd = (captable, fundId) => {
  if (!captable) return null;
  const captable_fund = captable.captable_funds.find(
    (cf) => cf.fund === fundId,
  );
  if (captable_fund) {
    return captable_fund.nfd;
  }
  return null;
};

const ring_fund_fd = (captable, fundId) => {
  if (!captable) return null;
  const captable_fund = captable.captable_funds.find(
    (cf) => cf.fund === fundId,
  );
  if (captable_fund) {
    return captable_fund.fd;
  }
  return null;
};

const ring_other_fund_nfd = (captable, fundId) => {
  if (!captable) return null;
  const captable_fund = captable.captable_funds.filter(
    (cf) => cf.fund !== fundId,
  );
  if (captable_fund.length > 0) {
    return captable_fund.reduce((acc, cf) => acc + cf.nfd, 0);
  }
  return 0;
};

const ring_other_fund_fd = (captable, fundId) => {
  debugger;
  if (!captable) return null;
  const captable_fund = captable.captable_funds.filter(
    (cf) => cf.fund !== fundId,
  );
  if (captable_fund.length > 0) {
    return captable_fund.reduce((acc, cf) => acc + cf.fd, 0);
  }
  return 0;
};

const valueFormatter = simpleNumberFormatter(2, 2, 100, " %");

export const CaptableChart = ({ captable, fundId }) => {
  const fund = useBackofficeConstant("backoffice.Fund", fundId);
  const { t } = useTranslation("translation");
  const labels =
    captable &&
    [
      {
        display: t("Founders + Mgt."),
        value: captable.managers_nfd,
        color: "#148668",
      },
      {
        display: `Ring - ${fund.name}`,
        value: ring_fund_nfd(captable, fundId),
        color: "#94DC5C",
      },
      {
        display: t("Ring - other funds"),
        value: ring_other_fund_nfd(captable, fundId),
        color: "#94DC5C",
      },
      {
        display: t("Funds"),
        value: captable.ring_lps_nfd + captable.other_funds_nfd,
        color: "#C9F1A5",
      },
      {
        display: t("BA and others"),
        value: captable.ba_and_others_nfd,
        color: "#0E463A",
      },
    ].filter((l) => l.value > 0);
  const config = labels && {
    data: {
      labels: labels.map((l) => l.display),
      datasets: [
        {
          data: labels.map((l) => l.value),
          backgroundColor: labels.map((l) => l.color),
        },
      ],
    },
    options: {
      aspectRatio: 1,
      plugins: {
        legend: {
          position: "right",
          display: false, // replaced by html legend for html2canvas
        },
      },
      animation: { duration: 0 },
    },
  };

  if (!config || !captable) {
    return (
      <Grid container spacing={1} maxWidth={350}>
        <Grid
          size={12}
          height={80}
          alignItems={"center"}
          display="flex"
          justifyContent={"center"}
        >
          <Typography variant="body1">No captable data available</Typography>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container spacing={1} maxWidth={350}>
      <Grid
        size={5}
        display={"flex"}
        justifyContent={"center"}
        alignItems={"center"}
        maxHeight={80}
      >
        <Pie data={config.data} options={config.options} />
      </Grid>
      <Grid
        size={7}
        alignItems={"center"}
        display="flex"
        flexDirection="row"
        justifyContent={"right"}
      >
        <Box>
          {labels.map((l) => {
            return (
              <Box
                key={l.display}
                sx={{ display: "flex", alignItems: "center" }}
              >
                <Box
                  sx={{
                    flexGrow: 0,
                    flexShrink: 0,
                    width: 10,
                    height: 10,
                    backgroundColor: l.color,
                    marginRight: 1,
                  }}
                />
                <Typography variant="caption">
                  {l.display} ({valueFormatter(l.value)})
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Grid>
    </Grid>
  );
};

export const CaptableFDInfo = ({ captable, fundId }) => {
  const fund = useBackofficeConstant("backoffice.Fund", fundId);
  if (!captable) {
    return (
      <Grid container spacing={1} maxWidth={350}>
        <Grid
          size={12}
          height={30}
          alignItems={"center"}
          display="flex"
          justifyContent={"center"}
        >
          <Typography variant="body1">No captable data available</Typography>
        </Grid>
      </Grid>
    );
  }

  return (
    <>
      <SlideLabelValue label={`Ring - ${fund.name}`}>
        {valueFormatter(ring_fund_fd(captable, fundId))}
      </SlideLabelValue>
      {ring_other_fund_fd(captable, fundId) > 0 && (
        <SlideLabelValue label={t("Ring - other funds")}>
          {valueFormatter(ring_other_fund_fd(captable, fundId))}
        </SlideLabelValue>
      )}
      <SlideLabelValue label={t("Founders + Mgt.")}>
        {valueFormatter(captable.managers_fd)}
      </SlideLabelValue>
    </>
  );
};

const CaptableWidget = ({ captable, fundId }) => {
  return (
    <Box width={350} marginX={"auto"}>
      <SlideStyle>
        <Paper elevation={3} sx={{ padding: 1 }}>
          <SlideSectionText size={4}>
            <Typography variant="h3">
              {t("Capitalization table (non fully-diluted)")}
            </Typography>
            <CaptableChart captable={captable} fundId={fundId} />
            <Typography variant="h3" marginBottom={0}>
              {t("Fully diluted information")}
            </Typography>
            <CaptableFDInfo captable={captable} fundId={fundId} />
          </SlideSectionText>
        </Paper>
      </SlideStyle>
    </Box>
  );
};

export const CaptableMultiFundWidget = ({ captable }) => {
  const funds = useBackofficeConstantDico("backoffice.Fund");
  if (
    !captable ||
    !captable.captable_funds ||
    captable.captable_funds.length === 0
  ) {
    return (
      <Box maxWidth={"100%"} sx={{ overflowX: "auto" }}>
        <Typography variant="body1">
          {t("No captable data available")}
        </Typography>
      </Box>
    );
  }

  return (
    <Box maxWidth={"100%"} sx={{ overflowX: "auto" }}>
      <Stack
        direction={"row"}
        spacing={2}
        flexWrap={"nowrap"}
        padding={1}
        width={"fit-content"}
      >
        {captable.captable_funds.map((df, index) => (
          <Box display={"inline"} key={index}>
            <Typography variant="h6" sx={{ textAlign: "center" }}>
              {funds[df.fund]?.name || t("Unknown Fund")}
            </Typography>
            <CaptableWidget captable={captable} fundId={df.fund} />
          </Box>
        ))}
      </Stack>
    </Box>
  );
};

export default CaptableWidget;
